import React, { useState, useEffect, useCallback } from 'react';
import { Property } from '../../types/property.ts';
import { formatCurrency, formatDate, formatPhoneNumber } from '../../utils/formatters.ts';
import MaintenanceRecords from './MaintenanceRecords.tsx';
import { maintenanceService } from '../../services/maintenanceService.ts';
import '../../styles/PropertyDetails.css'

interface PropertyDetailsProps {
  property: Property;
  onClose: () => void;
}

const PropertyDetails: React.FC<PropertyDetailsProps> = ({ property, onClose }) => {
  const [monthlyMaintenance, setMonthlyMaintenance] = useState(0);
  const [yearlyMaintenance, setYearlyMaintenance] = useState(0);

  const fetchMaintenanceCosts = useCallback(async () => {
    try {
      const monthCost = await maintenanceService.getMaintenanceCosts(property.id, 'month');
      const yearCost = await maintenanceService.getMaintenanceCosts(property.id, 'year');
      setMonthlyMaintenance(monthCost);
      setYearlyMaintenance(yearCost);
    } catch (error) {
      console.error('Error fetching maintenance costs:', error);
    }
  }, [property.id]);

  // Refresh maintenance costs when a record is added/updated
  const handleMaintenanceChange = async () => {
    await fetchMaintenanceCosts();
  };

  useEffect(() => {
    fetchMaintenanceCosts();
  }, [fetchMaintenanceCosts]);

  const getStatusClass = (status: string) => {
    return `status-badge status-${status.toLowerCase()}`;
  };

  return (
    <div className="modal" onClick={onClose}>
      <div className="property-details-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{property.name}</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="details-content">
          <div className="detail-section">
            <h3>📋 Basic Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Status</label>
                <span className={getStatusClass(property.status)}>
                  <span className="status-dot"></span>
                  {property.status}
                </span>
              </div>
              <div className="detail-item">
                <label>Type</label>
                <span>{property.property_type}</span>
              </div>
              <div className="detail-item">
                <label>Floor/Unit</label>
                <span>{property.floor_unit}</span>
              </div>
              <div className="detail-item">
                <label>Area</label>
                <span>{property.area} sq.ft</span>
              </div>
              <div className="detail-item">
                <label>Planned Rent</label>
                <span>{formatCurrency(property.planned_rent)}</span>
              </div>
              <div className="detail-item">
                <label>Added On</label>
                <span>{formatDate(property.date_added)}</span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>📍 Location</h3>
            <p className="address">{property.address}</p>
          </div>

          <div className="detail-section">
            <h3>✨ Amenities</h3>
            <div className="amenities-list">
              {property.amenities && property.amenities.length > 0 ? (
                property.amenities.map((amenity, index) => (
                  <span key={index} className="amenity-tag">{amenity}</span>
                ))
              ) : (
                <p className="no-amenities">No amenities listed for this property</p>
              )}
            </div>
          </div>

          {property.tenant && (
            <div className="detail-section">
              <h3>👤 Current Tenant</h3>
              <div className="detail-content">
                <p><strong>Name:</strong> {property.tenant.full_name}</p>
                <p><strong>Contact:</strong> {formatPhoneNumber(property.tenant.phone)}</p>
                <p><strong>Monthly Rent:</strong> {formatCurrency(property.tenant.monthly_rent)}</p>
                <p>
                  <strong>Status:</strong> 
                  <span className={`${getStatusClass(property.tenant.status)}`}>
                    <span className="status-dot"></span>
                    {property.tenant.status}
                  </span>
                </p>
              </div>
            </div>
          )}

          {property.notes && (
            <div className="detail-section">
              <h3>📝 Notes</h3>
              <p className="notes">{property.notes}</p>
            </div>
          )}

          <div className="detail-section">
            <h3>💰 Maintenance Overview</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>This Month's Costs</label>
                <span className="maintenance-cost">
                  {formatCurrency(monthlyMaintenance)}
                </span>
              </div>
              <div className="detail-item">
                <label>This Year's Costs</label>
                <span className="maintenance-cost">
                  {formatCurrency(yearlyMaintenance)}
                </span>
              </div>
            </div>
          </div>

          {/* Maintenance Records Section */}
          <MaintenanceRecords 
            propertyId={property.id} 
            onMaintenanceChange={handleMaintenanceChange}
          />
        </div>

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetails;
